import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, navigate } from 'gatsby';
import posthog from 'posthog-js';
import { BsLightningChargeFill } from 'react-icons/bs';

import searchIcon from '../../../assets/icons/search_black_24dp.svg';
import { useSearchSuggestions } from '@src/hooks/search';
import { buildSuggestionSearchPath } from '@src/utils/searchSuggestions';
import { buildQuerySearchPath } from '@src/utils/searchQueryFilters';
import { SearchSuggestion } from '@src/settings/searchSuggestions';
import { sendEvent } from '@src/utils/gtag';
import * as styles from './HeaderSearch.module.css';

const LISTBOX_ID = 'header-search-suggestions';
const optionId = (index: number) => `${LISTBOX_ID}-${index}`;

// Every typed word must appear in the label, so "edexcel a level" still matches.
const matches = (suggestion: SearchSuggestion, term: string) => {
  const label = suggestion.label.toLowerCase();
  return term
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => label.includes(word));
};

export const HeaderSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { background, isSignedIn, suggestions } = useSearchSuggestions();

  const visibleSuggestions = useMemo(
    () => suggestions.filter((suggestion) => matches(suggestion, searchTerm)),
    [suggestions, searchTerm],
  );

  const isExpanded = isOpen && visibleSuggestions.length > 0;

  // The list scrolls, so keep the option picked with the arrow keys in view.
  useEffect(() => {
    if (isExpanded && activeIndex >= 0) {
      document.getElementById(optionId(activeIndex))?.scrollIntoView({ block: 'nearest' });
    }
  }, [isExpanded, activeIndex]);

  const close = () => {
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    posthog.capture('resource_search_submitted', { has_query: Boolean(searchTerm.trim()) });
    close();
    navigate(buildQuerySearchPath(searchTerm));
  };

  const selectSuggestion = (suggestion: SearchSuggestion) => {
    sendEvent({
      name: 'search_suggestion_clicked',
      category: 'search',
      label: suggestion.id,
      value: undefined,
    });
    setSearchTerm('');
    close();
    navigate(buildSuggestionSearchPath(suggestion));
  };

  const moveActive = (step: number) => {
    setIsOpen(true);
    setActiveIndex((index) => {
      const count = visibleSuggestions.length;
      if (!count) return -1;
      if (index === -1) return step > 0 ? 0 : count - 1;
      return (index + step + count) % count;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveActive(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveActive(-1);
        break;
      case 'Enter':
        if (isExpanded && activeIndex >= 0) {
          e.preventDefault();
          selectSuggestion(visibleSuggestions[activeIndex]);
        }
        break;
      case 'Escape':
        if (isExpanded) {
          e.preventDefault();
          close();
        }
        break;
      default:
    }
  };

  // Close when focus leaves the whole widget (tabbing away or clicking elsewhere), but not
  // when it moves to the "change course" link inside the panel.
  const handleBlur = (e: React.FocusEvent) => {
    if (!wrapperRef.current?.contains(e.relatedTarget as Node)) {
      close();
    }
  };

  return (
    <div className={styles.headerSearch} ref={wrapperRef} onBlur={handleBlur}>
      <form className="nav-search-form" onSubmit={handleSubmit} role="search">
        <input
          type="search"
          className="nav-search-input"
          placeholder="Search past papers, topic tests, cheat sheets…"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          aria-label="Search"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isExpanded}
          aria-controls={LISTBOX_ID}
          aria-activedescendant={isExpanded && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        />
        <button type="submit" className="nav-search-button" aria-label="Submit search">
          <img src={searchIcon} alt="" />
        </button>
      </form>

      {isExpanded && (
        // Stops the input losing focus when an option is pressed, so the click lands.
        <div className={styles.panel} onMouseDown={(e) => e.preventDefault()}>
          <p className={styles.heading} id={`${LISTBOX_ID}-heading`}>
            {isSignedIn ? 'Suggested for you' : 'Popular searches'}
          </p>
          <ul
            className={styles.list}
            id={LISTBOX_ID}
            role="listbox"
            aria-labelledby={`${LISTBOX_ID}-heading`}
          >
            {visibleSuggestions.map((suggestion, index) => (
              <li
                key={suggestion.id}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                className={`${styles.option} ${index === activeIndex ? styles.active : ''}`}
                onClick={() => selectSuggestion(suggestion)}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <BsLightningChargeFill className={styles.optionIcon} aria-hidden="true" />
                {suggestion.label}
              </li>
            ))}
          </ul>
          {isSignedIn && (
            <p className={styles.helper}>
              Based on the course in your profile ({background}).{' '}
              <Link to="/profile?section=profile" onClick={close}>
                Change course
              </Link>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderSearch;
